import React from 'react'

import {
  Banner,
  Button,
  ButtonClean,
  ButtonGroup,
  ConfirmModal,
  HeadContent,
  Skeleton,
  Subsection,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import type { Money } from '@fourthwall/services/dist/models/common'
import { getPrice } from '@fourthwall/utils/dist/common'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import { isWebViewUserAgent } from '@fourthwall/webview-bridge/utils'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { routing } from '@utils/routing'

import {
  AddSubscriptionModal,
  UpgradeSubscriptionBanner,
  getSubscriptionSamplesCreditGrant,
  useSubscription,
  useTopSubscriptionPlan,
} from '../../../../modules/Subscriptions'
import * as S from './Samples.styled'
import { SamplesCreditBalance } from './components/SamplesCreditBalance/SamplesCreditBalance'

export type SamplesCreditViewProps = {
  credit: Money | undefined
  products: { status: 'NO_PRODUCTS' | 'LOADING' | 'AVAILABLE' | 'HIDDEN'; onManage?: () => void }
  onOrderCreate: () => void
}

export const SamplesView = ({ credit, products, onOrderCreate }: SamplesCreditViewProps) => {
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const {
    undoCancelActiveSubscription,
    hasActiveProSubscription,
    pendingSubscription,
    activeSubscription,
    canUpgradeSubscription,
  } = useSubscription()
  const { topSubscriptionPlan } = useTopSubscriptionPlan()
  const { isWebView } = useWebViewBridge()
  const canReactivate =
    !pendingSubscription &&
    activeSubscription &&
    activeSubscription.scheduledCancelledAtTheEndOfPeriod

  const getSamplesCreditAmount = () => {
    if (!topSubscriptionPlan) return '$10/month'

    const value = topSubscriptionPlan?.creditForSamples.value || 10
    const period = topSubscriptionPlan?.recurringPeriod === 'YEARLY' ? 'year' : 'month'

    return `${getPrice(value, { currency: topSubscriptionPlan?.creditForSamples.currency })}/${period}`
  }

  const handleUpgradeSubscription = () => {
    open(AddSubscriptionModal, {
      title: `Upgrade to Pro and receive ${getSamplesCreditAmount()} in sample credit`,
      source: 'settings_samples',
    })
  }

  const renderUpgradeSubscriptionBanner = () => {
    if (isWebView) return null

    if (canReactivate || !canUpgradeSubscription) return null

    return (
      <S.Container>
        <UpgradeSubscriptionBanner source="settings_samples" onClick={handleUpgradeSubscription}>
          Upgrade to <strong>Pro</strong> and receive {getSamplesCreditAmount()} in sample credit.
        </UpgradeSubscriptionBanner>
      </S.Container>
    )
  }

  const openUndoCancelSubscriptionConfirmModal = () => {
    const title = activeSubscription?.activeTo ? (
      <>
        You will automatically renew Pro plan on{' '}
        <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />
      </>
    ) : null

    open(ConfirmModal, {
      title: 'Your subscription is scheduled to be canceled. Would you like to undo this?',
      text: title,
      cancelLabel: 'Keep canceled',
      confirmLabel: 'Renew subscription',
      onConfirm: async () => {
        await undoCancelActiveSubscription()
      },
    })
  }

  const renderReactivateSubscriptionBanner = () => {
    if (isWebView) return null

    if (!canReactivate || !activeSubscription || !activeSubscription.activeTo) return null

    return (
      <S.Container>
        <UpgradeSubscriptionBanner
          source="settings_samples"
          buttonLabel="Reactivate"
          onClick={openUndoCancelSubscriptionConfirmModal}
        >
          Get{' '}
          {getSubscriptionSamplesCreditGrant({
            subscriptionPlan: activeSubscription.plan,
            withPeriod: false,
          })}{' '}
          in free credit every{' '}
          {activeSubscription.plan.recurringPeriod === 'YEARLY' ? 'year' : 'month'} with{' '}
          <strong>Fourthwall Pro</strong>! Reactivate your subscription by{' '}
          <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" /> to claim
          your bonus.
        </UpgradeSubscriptionBanner>
      </S.Container>
    )
  }

  const renderSubscriptionGrantBanner = () => {
    if (isWebView) return null

    if (
      !hasActiveProSubscription ||
      !activeSubscription ||
      !activeSubscription.activeTo ||
      !activeSubscription?.plan.creditForSamples.value ||
      canReactivate
    ) {
      return null
    }

    if (
      !pendingSubscription &&
      activeSubscription &&
      activeSubscription.scheduledCancelledAtTheEndOfPeriod
    ) {
      return (
        <S.Container>
          <Banner appearance="pro">
            You would receive{' '}
            {getSubscriptionSamplesCreditGrant({
              subscriptionPlan: activeSubscription?.plan,
            })}{' '}
            of free sample credit from <strong>Fourthwall Pro</strong>.{' '}
            <ButtonClean
              appearance="inherit"
              label="Reactivate subscription"
              onClick={openUndoCancelSubscriptionConfirmModal}
            />{' '}
            to receive{' '}
            {getSubscriptionSamplesCreditGrant({
              subscriptionPlan: activeSubscription?.plan,
              withPeriod: false,
            })}{' '}
            on <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />
            .{' '}
          </Banner>
        </S.Container>
      )
    }

    return (
      <S.Container>
        <Banner appearance="pro">
          You're receiving{' '}
          {getSubscriptionSamplesCreditGrant({
            subscriptionPlan: activeSubscription?.plan,
          })}{' '}
          of free sample credit from <strong>Fourthwall Pro</strong>.{' '}
          {getSubscriptionSamplesCreditGrant({
            subscriptionPlan: activeSubscription?.plan,
            withPeriod: false,
          })}{' '}
          will be added on{' '}
          <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />
        </Banner>
      </S.Container>
    )
  }

  const renderProducts = () => {
    if (products.status === 'HIDDEN') {
      return null
    }

    if (products.status === 'LOADING') {
      return (
        <div style={{ marginTop: 16 }}>
          <Skeleton>
            <Skeleton.Shape width={media.tablet ? 180 : '100%'} height={48} />
          </Skeleton>
        </div>
      )
    }

    if (products.status === 'NO_PRODUCTS') {
      return (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            You don't have any products that you can order samples for (only products without
            minimums are currently able to be ordered as samples)
          </div>
          <Button appearance="primary" label="Manage your products" onClick={products.onManage} />
        </div>
      )
    }

    if (products.status === 'AVAILABLE') {
      return (
        <div style={{ marginTop: 16 }}>
          <ButtonGroup fullWidth={!media.tablet}>
            <Button label="Create a new order" onClick={onOrderCreate} />
          </ButtonGroup>
        </div>
      )
    }
  }

  return (
    <Subsection title={isWebViewUserAgent(navigator.userAgent) ? '' : 'Samples'}>
      <HeadContent title="Samples &ndash; Settings" />

      {renderUpgradeSubscriptionBanner()}
      {renderReactivateSubscriptionBanner()}
      {renderSubscriptionGrantBanner()}
      <p>
        Order pre-production samples of your products for quality checks and promotion. You can use
        sample credit (if available) or a credit card to pay for your samples.{' '}
        <ButtonClean
          label="Learn more"
          href={routing.external.helpCenter.orderingSamples}
          target="_blank"
        />
      </p>
      <p style={{ marginTop: 12, fontWeight: 600 }}>
        {credit ? (
          <SamplesCreditBalance label="Your current samples credit" balance={credit} />
        ) : (
          <Skeleton>
            <Skeleton.Shape width={200} />
          </Skeleton>
        )}
      </p>
      {renderProducts()}
    </Subsection>
  )
}
